import { defineStore } from 'pinia';
import { axios } from '@boot/axios.js';
import { getCurrentTimeInTimezone, isShiftActive } from '@utils/time.js';

/**
 * Facilities store for managing facility-related data.
 */
export const useFacilitiesStore = defineStore('facilities', {
    state: () => ({
        shifts: {}
    }),

    getters: {
        /**
         * Get shifts for a specific facility.
         * @param {number|string} facilityId - The facility id to retrieve shifts for.
         * @returns {Array|null} Returns the array of shift objects for the facility (may be empty if no shifts exist), or null if shifts for the facility have not yet been fetched.
         */
        getShiftsByFacility: state => facilityId => {
            if (facilityId in state.shifts) {
                return state.shifts[facilityId];
            }

            return null;
        },

        /**
         * Check if there's an active shift for the current time for a facility.
         * @param {Object} _ - State (unused, accessing root store)
         * @returns {Function} Function that accepts facilityId and facilityTimezone
         */
        hasActiveShift:
            state =>
            (facilityId, facilityTimezone = 'UTC') => {
                if (!(facilityId in state.shifts)) {
                    return null; // Shifts not yet loaded
                }

                const shifts = state.shifts[facilityId];

                if (shifts.length === 0) {
                    return false; // No shifts defined
                }

                // Get current time and day in the facility's timezone
                const { currentTime, currentDayKey } =
                    getCurrentTimeInTimezone(facilityTimezone);

                // Check if any shift is active for current day and time
                for (const shift of shifts) {
                    if (isShiftActive(shift, currentTime, currentDayKey)) {
                        return true;
                    }
                }

                return false; // No active shift found
            }
    },

    actions: {
        /**
         * Resets the state to its default values.
         */
        resetState() {
            this.$reset();
        },

        /**
         * Load shifts for a facility from server.
         * @param {number|string} facilityId - The id of the facility to load shifts for.
         * @returns {Promise<Array>} Promise resolving to array of shift objects.
         */
        async loadShiftsAction(facilityId) {
            try {
                const response = await axios.get(
                    `/facilities/${facilityId}/shifts/`
                );
                const shifts = response.data.results;
                // Sort shifts by start time
                shifts.sort((a, b) => a.start_hour.localeCompare(b.start_hour));
                // Store shifts
                this.shifts[facilityId] = shifts;
                return this.shifts[facilityId];
            } catch (error) {
                console.error('Failed to load shifts due to: ', error);
                throw error;
            }
        }
    }
});
